import { api } from '@udm/contracts';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, Loader2, Pencil, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, request } from '../../api/client';
import { Card } from '../../ui/components';
import { sendCommand, settled } from './commands';
import {
  DAYS,
  nextStepId,
  parseProgramRead,
  programChanges,
  validateProgram,
  type Day,
  type LightProgram,
  type ProgramRead,
  type Step,
} from './lightProgram';
import { NumberBox, SwitchEdit, TimeEdit } from './ReadResult';
import './LightProgramCard.css';

export const PROGRAM_READ = 'light_program.read';
const PROGRAM_WRITE = 'light_program.write';
/** The gateway runs the whole program write (several exchanges and a read-back) as one command. */
const WRITE_WAIT_MS = 120_000;

type Outcome = { ok: true } | { ok: false; code: string };

/**
 * Autonomous light program of the device, read and edited in human values (user request 2026-10-12, superadmin only:
 * the caller needs device.configure, held by the platform administrator alone). The program runs in the device without
 * the server. Reading and writing go through the protocol-free capabilities light_program.read / light_program.write;
 * the gateway translates them and confirms a write by reading the program back.
 */
export function LightProgramCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const writesQ = useQuery({
    queryKey: ['device', id, 'writes'],
    queryFn: () =>
      request('GET', `/api/v1/devices/${id}/writes`, undefined, (v) => api.DeviceWrites.parse(v)),
    staleTime: 10 * 60_000,
  });
  const canWrite = useMemo(
    () => (writesQ.data?.items ?? []).some((i) => i.key === PROGRAM_WRITE),
    [writesQ.data],
  );
  const [read, setRead] = useState<ProgramRead | null>(null);
  const [draft, setDraft] = useState<LightProgram | null>(null);
  const [busy, setBusy] = useState<'read' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const codeOf = (e: unknown) => (e instanceof ApiRequestError ? e.code : 'NETWORK_ERROR');

  const readDevice = async (): Promise<ProgramRead> => {
    const r = await request(
      'POST',
      `/api/v1/devices/${id}/queries`,
      { query_key: PROGRAM_READ, params: {} },
      (v) => api.DeviceQueryResponse.parse(v),
    );
    if (!r.ok || !r.result) throw new ApiRequestError(200, r.error_code ?? 'READ_FAILED');
    return parseProgramRead(r.result);
  };

  const load = async () => {
    setBusy('read');
    setError(null);
    setOutcome(null);
    setDraft(null);
    try {
      setRead(await readDevice());
    } catch (e) {
      setError(codeOf(e));
    } finally {
      setBusy(null);
    }
  };

  const shown = draft ?? read?.program ?? null;
  const pending = read && draft ? programChanges(read.program, draft) : 0;
  const errors = read && draft ? validateProgram(draft, read.limits) : [];

  const save = async () => {
    if (!read || !draft || !pending || errors.length) return;
    setBusy('save');
    setOutcome(null);
    try {
      const c = await settled(await sendCommand(id, PROGRAM_WRITE, { program: draft }), WRITE_WAIT_MS);
      if (c.status !== 'SUCCESS') {
        setOutcome({ ok: false, code: c.errorCode ?? c.status });
        return;
      }
      setRead(await readDevice());
      setDraft(null);
      setOutcome({ ok: true });
    } catch (e) {
      setOutcome({ ok: false, code: codeOf(e) });
    } finally {
      setBusy(null);
    }
  };

  const notices = read?.notices;
  return (
    <Card
      title={t('devices.program.title')}
      actions={
        <button type="button" className="btn" onClick={load} disabled={busy !== null}>
          {busy === 'read' ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}{' '}
          {t(read ? 'devices.program.reread' : 'devices.program.read')}
        </button>
      }
    >
      {!read && !error && <p className="muted small">{t('devices.program.intro')}</p>}
      {error && <p className="program-error">{t('devices.program.readFailed', { code: error })}</p>}
      {shown && read && <ProgramView p={shown} limit={read.limits.steps} edit={draft ? setDraft : null} />}
      {read && !draft && (
        <div className="toolbar" style={{ marginTop: 'var(--space-3)' }}>
          {canWrite ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setOutcome(null);
                setDraft(structuredClone(read.program));
              }}
            >
              <Pencil size={14} /> {t('devices.program.edit')}
            </button>
          ) : (
            <span className="muted small">{t('devices.program.notEnabled')}</span>
          )}
        </div>
      )}
      {notices && (notices.steps_not_running.length > 0 || notices.approximated) && (
        <p className="muted small">
          <CircleAlert size={13} />{' '}
          {notices.steps_not_running.length > 0 &&
            t('devices.program.idleSteps', { list: notices.steps_not_running.join(', ') })}{' '}
          {notices.approximated && t('devices.program.offModel')}
        </p>
      )}
      {draft && read && (
        <div className="ro-editbar" role="region" aria-label={t('devices.program.changes')}>
          <strong>
            {pending ? t('devices.program.pending', { count: pending }) : t('devices.program.noChanges')}
          </strong>
          {errors.map((e) => (
            <span key={e} className="program-error">
              {t(`devices.program.error.${e}` as 'devices.program.error.time', {
                max: read.limits.steps,
                lux: read.limits.max_lux,
              })}
            </span>
          ))}
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={busy !== null || !pending || errors.length > 0}
            >
              {busy === 'save' ? <Loader2 size={14} className="spin" /> : <Save size={14} />}{' '}
              {t('devices.program.save')}
            </button>
            <button type="button" className="btn" onClick={() => setDraft(null)} disabled={busy !== null}>
              <X size={14} /> {t('devices.program.cancel')}
            </button>
          </div>
        </div>
      )}
      {outcome && (
        <div className="ro-outcome" role="status">
          {outcome.ok ? (
            <span className="ro-alarm">
              <CircleCheck size={14} /> {t('devices.program.saved')}
            </span>
          ) : (
            <span className="program-error">
              <CircleAlert size={14} />{' '}
              {outcome.code === 'READBACK_MISMATCH'
                ? t('devices.program.mismatch')
                : t('devices.program.failed', { code: outcome.code })}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

const pctText = (n: number, off: string) => (n === 0 ? off : `${n} %`);

function ProgramView({
  p,
  limit,
  edit,
}: {
  p: LightProgram;
  limit: number;
  edit: ((p: LightProgram) => void) | null;
}) {
  const { t } = useTranslation();
  const off = t('devices.program.off');
  const put = (patch: Partial<LightProgram>) => edit?.({ ...p, ...patch });
  const putBand = (i: number, patch: Partial<LightProgram['bands'][number]>) =>
    put({ bands: p.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const putStep = (i: number, patch: Partial<Step>) =>
    put({ steps: p.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const last = p.bands.length - 1;
  const boundary = (i: number) =>
    edit ? (
      <NumberBox
        v={p.bands[i]!.up_to_lux ?? NaN}
        onChange={(v) => putBand(i, { up_to_lux: v })}
        lim={{ min: 1, step: 1 }}
        unit="lx"
        label={t('devices.program.boundary', { n: i + 1 })}
      />
    ) : (
      `${p.bands[i]!.up_to_lux} lx`
    );
  const bandLabel = (i: number): ReactNode => {
    if (i === 0)
      return (
        <>
          {t('devices.program.below')} {boundary(0)}
        </>
      );
    if (i === last)
      return (
        <>
          {t('devices.program.above')} {p.bands[i - 1]!.up_to_lux} lx
        </>
      );
    return (
      <>
        {p.bands[i - 1]!.up_to_lux} – {boundary(i)}
      </>
    );
  };
  const nextId = nextStepId(p.steps, limit);

  return (
    <div className="program">
      <h4 className="program-h">{t('devices.program.byLight')}</h4>
      <div className="program-line">
        {edit && <span>{t('devices.program.sensor')}</span>}
        {edit ? (
          <SwitchEdit
            on={p.sensor.enabled}
            onChange={(v) => put({ sensor: { ...p.sensor, enabled: v } })}
            label={t('devices.program.sensor')}
          />
        ) : (
          <strong>{t(p.sensor.enabled ? 'devices.program.sensorOn' : 'devices.program.sensorOff')}</strong>
        )}
        <span>{t('devices.program.between')}</span>
        {edit ? (
          <TimeEdit
            v={p.sensor.from}
            onChange={(v) => put({ sensor: { ...p.sensor, from: v } })}
            label={t('devices.program.from')}
          />
        ) : (
          <strong>{p.sensor.from}</strong>
        )}
        <span>{t('devices.program.and')}</span>
        {edit ? (
          <TimeEdit
            v={p.sensor.to}
            onChange={(v) => put({ sensor: { ...p.sensor, to: v } })}
            label={t('devices.program.to')}
          />
        ) : (
          <strong>{p.sensor.to}</strong>
        )}
      </div>
      <table className="ro-table">
        <thead>
          <tr>
            <th>{t('devices.program.light')}</th>
            <th>{t('devices.program.level')}</th>
          </tr>
        </thead>
        <tbody>
          {p.bands.map((b, i) => (
            <tr key={i}>
              <td>{bandLabel(i)}</td>
              <td>
                {edit ? (
                  <NumberBox
                    v={b.level_pct}
                    onChange={(v) => putBand(i, { level_pct: v })}
                    lim={{ min: 0, max: 100, step: 1 }}
                    unit="%"
                    label={t('devices.program.bandLevel', { n: i + 1 })}
                  />
                ) : (
                  pctText(b.level_pct, off)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="program-line muted small">
        {t('devices.program.tolerance')}{' '}
        {edit ? (
          <NumberBox
            v={p.tolerance_lux}
            onChange={(v) => put({ tolerance_lux: v })}
            lim={{ min: 1, step: 1 }}
            unit="lx"
            label={t('devices.program.tolerance')}
          />
        ) : (
          <strong>± {p.tolerance_lux} lx</strong>
        )}
        <span>{t('devices.program.toleranceHint')}</span>
      </div>

      <h4 className="program-h">{t('devices.program.steps')}</h4>
      {!p.steps.length && !edit && <p className="muted small">{t('devices.program.noSteps')}</p>}
      {p.steps.length > 0 && (
        <div className="program-scroll">
          <table className="ro-table">
            <thead>
              <tr>
                <th>{t('devices.program.at')}</th>
                <th>{t('devices.program.level')}</th>
                <th>{t('devices.program.days')}</th>
                <th>{t('devices.program.active')}</th>
                {edit && <th />}
              </tr>
            </thead>
            <tbody>
              {p.steps.map((s, i) => (
                <tr key={s.id}>
                  <td>
                    {edit ? (
                      <TimeEdit
                        v={s.time}
                        onChange={(v) => putStep(i, { time: v })}
                        label={t('devices.program.stepAt', { n: i + 1 })}
                      />
                    ) : (
                      s.time
                    )}
                  </td>
                  <td>
                    {edit ? (
                      <NumberBox
                        v={s.level_pct}
                        onChange={(v) => putStep(i, { level_pct: v })}
                        lim={{ min: 0, max: 100, step: 1 }}
                        unit="%"
                        label={t('devices.program.stepLevel', { n: i + 1 })}
                      />
                    ) : (
                      pctText(s.level_pct, off)
                    )}
                  </td>
                  <td>
                    <DaysPicker days={s.days} onChange={edit ? (d) => putStep(i, { days: d }) : null} />
                  </td>
                  <td>
                    {edit ? (
                      <SwitchEdit
                        on={s.enabled}
                        onChange={(v) => putStep(i, { enabled: v })}
                        label={t('devices.program.stepActive', { n: i + 1 })}
                      />
                    ) : (
                      t(s.enabled ? 'devices.program.yes' : 'devices.program.no')
                    )}
                  </td>
                  {edit && (
                    <td>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={t('devices.program.removeStep', { n: i + 1 })}
                        onClick={() => put({ steps: p.steps.filter((_, j) => j !== i) })}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && nextId !== undefined && (
        <button
          type="button"
          className="btn"
          onClick={() =>
            put({
              steps: [
                ...p.steps,
                { id: nextId, time: '23:00', level_pct: 20, days: [...DAYS], enabled: true },
              ],
            })
          }
        >
          <Plus size={14} /> {t('devices.program.addStep')}
        </button>
      )}

      {p.power_on_level_pct !== null && (
        <>
          <h4 className="program-h">{t('devices.program.powerOn')}</h4>
          <div className="program-line">
            {edit ? (
              <NumberBox
                v={p.power_on_level_pct}
                onChange={(v) => put({ power_on_level_pct: v })}
                lim={{ min: 0, max: 100, step: 1 }}
                unit="%"
                label={t('devices.program.powerOn')}
              />
            ) : (
              <strong>{pctText(p.power_on_level_pct, off)}</strong>
            )}
            <span className="muted small">{t('devices.program.powerOnHint')}</span>
          </div>
        </>
      )}

      <h4 className="program-h">{t('devices.program.day')}</h4>
      <DayTimeline p={p} />
    </div>
  );
}

function DaysPicker({ days, onChange }: { days: Day[]; onChange: ((d: Day[]) => void) | null }) {
  const { t } = useTranslation();
  if (!onChange)
    return (
      <>
        {days.length === DAYS.length
          ? t('devices.program.everyDay')
          : days.map((d) => t(`devices.program.dayShort.${d}`)).join(' ')}
      </>
    );
  return (
    <span className="program-days" role="group" aria-label={t('devices.program.days')}>
      {DAYS.map((d) => {
        const on = days.includes(d);
        return (
          <button
            key={d}
            type="button"
            className={`program-day${on ? ' is-on' : ''}`}
            aria-pressed={on}
            title={t(`devices.program.dayLong.${d}`)}
            onClick={() =>
              onChange(on ? days.filter((x) => x !== d) : DAYS.filter((x) => x === d || days.includes(x)))
            }
          >
            {t(`devices.program.dayShort.${d}`)}
          </button>
        );
      })}
    </span>
  );
}

/** The program's day in time order, as sentences (sensor start / end and the timed steps). */
function DayTimeline({ p }: { p: LightProgram }) {
  const { t } = useTranslation();
  const off = t('devices.program.off');
  type Ev = { time: string; order: number; text: string };
  const ev: Ev[] = [];
  if (p.sensor.enabled) {
    ev.push({ time: p.sensor.from, order: 1, text: t('devices.program.evSensorStart') });
    // at the same minute a timed step wins over the end of the sensor hours (observed on the desk test device)
    ev.push({ time: p.sensor.to, order: 0, text: t('devices.program.evSensorEnd') });
  }
  for (const s of p.steps)
    if (s.enabled)
      ev.push({
        time: s.time,
        order: 2,
        text:
          s.days.length === DAYS.length
            ? t('devices.program.evStep', { level: pctText(s.level_pct, off) })
            : t('devices.program.evStepDays', {
                level: pctText(s.level_pct, off),
                days: s.days.map((d) => t(`devices.program.dayShort.${d}`)).join(' '),
              }),
      });
  ev.sort((a, b) => a.time.localeCompare(b.time) || a.order - b.order);
  if (!ev.length) return <p className="muted small">{t('devices.program.evNone')}</p>;
  return (
    <ul className="program-timeline">
      {ev.map((e, i) => (
        <li key={i}>
          <strong>{e.time}</strong> {e.text}
        </li>
      ))}
    </ul>
  );
}
